import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, useInView, AnimatePresence } from 'motion/react';
import { 
  Zap, MapPin, Users, TrendingUp, Sparkles, ArrowRight, 
  Phone, Fuel, ShoppingBag, Shield, MessageSquare, 
  Radio, DollarSign, Eye, ChevronDown, Play, Star,
  CheckCircle, Globe, BarChart3, Mic, Clock, Award,
  Target, TrendingDown, AlertTriangle, Navigation,
  Smartphone, Laptop, Tablet, Check, X, Menu,
  Twitter, Facebook, Instagram, Linkedin, Youtube,
  Lightbulb, Heart, Compass, Lock, Bell, Gift,
  Wifi, Battery, Signal, RefreshCw, Activity,
  ThumbsUp, MessageCircle, Share2, Bookmark,
  TrendingUpIcon, AlertCircle, Info, Map,
  Calendar, FileText, Image, Video, Headphones,
  Coffee, Briefcase, Home, Car, Bus, Gauge,
  CloudRain, Sun, Wind, Droplets, Flame,
  ShoppingCart, Package, Truck, CreditCard,
  UserCheck, UserPlus, Users2, Crown
} from 'lucide-react';
import { useNavigate } from 'react-router';

export default function PowerfulLanding() {
  const navigate = useNavigate();
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [activeState, setActiveState] = useState('Lagos');
  const [liveReports, setLiveReports] = useState<any[]>([]);
  const [email, setEmail] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const heroRef = useRef(null);
  const featuresRef = useRef(null);
  const statsRef = useRef(null);
  const isStatsInView = useInView(statsRef, { once: true });

  // Parallax effects
  const y1 = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const y2 = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.3], [1, 0.8]);

  const nigerianStates = [
    { name: 'Lagos', users: 4521, reports: 892, active: true },
    { name: 'Abuja (FCT)', users: 2134, reports: 445, active: true },
    { name: 'Port Harcourt', users: 987, reports: 234, active: true },
    { name: 'Kano', users: 756, reports: 156, active: true },
    { name: 'Ibadan', users: 645, reports: 178, active: true },
    { name: 'Enugu', users: 423, reports: 98, active: true },
    { name: 'Kaduna', users: 398, reports: 87, active: true },
    { name: 'Benin City', users: 312, reports: 76, active: true }
  ];

  const liveReportTemplates = [
    { type: 'traffic', icon: Car, color: 'text-red-400', text: 'Heavy traffic on Third Mainland Bridge', location: 'Lagos' },
    { type: 'power', icon: Zap, color: 'text-yellow-400', text: 'Power restored in Lekki Phase 1', location: 'Lagos' },
    { type: 'fuel', icon: Fuel, color: 'text-orange-400', text: 'Fuel available at Oando - ₦650/L', location: 'Abuja' },
    { type: 'market', icon: ShoppingCart, color: 'text-green-400', text: 'Rice @ ₦45,000/bag at Mile 12', location: 'Lagos' },
    { type: 'weather', icon: CloudRain, color: 'text-blue-400', text: 'Heavy rainfall expected in 20 mins', location: 'Port Harcourt' },
    { type: 'road', icon: AlertTriangle, color: 'text-red-400', text: 'Pothole on Airport Road causing delays', location: 'Kano' },
    { type: 'event', icon: Calendar, color: 'text-purple-400', text: 'Road closure for presidential convoy', location: 'Abuja' },
    { type: 'crowd', icon: Users, color: 'text-cyan-400', text: 'Low crowd at Ojuelegba bus stop', location: 'Lagos' }
  ];

  // Simulate live reports
  useEffect(() => {
    const generateReport = () => {
      const template = liveReportTemplates[Math.floor(Math.random() * liveReportTemplates.length)];
      return {
        ...template,
        id: Date.now() + Math.random(),
        time: 'Just now',
        upvotes: Math.floor(Math.random() * 50) + 5
      };
    };

    setLiveReports([generateReport(), generateReport(), generateReport()]);

    const interval = setInterval(() => {
      setLiveReports(prev => [generateReport(), ...prev].slice(0, 5));
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: Zap,
      title: 'Light Watch',
      subtitle: 'Power Outage Tracker',
      description: 'Never be caught off guard by power outages again. Real-time alerts from your neighbors help you plan ahead.',
      longDescription: 'Track power status in real-time across your neighborhood. Get instant notifications when NEPA strikes or when power is restored. Plan your day around power availability, schedule generator usage, and preserve your food and electronics. Our community-verified system shows power patterns over time, helping you predict outages.',
      gradient: 'from-yellow-500 via-orange-500 to-red-600',
      benefits: [
        'Real-time power status updates',
        'Neighborhood coverage maps',
        'Historical outage data & patterns',
        'Generator usage optimization',
        'Food preservation alerts',
        'Battery & UPS planning'
      ],
      image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?auto=format&fit=crop&q=80&w=1000',
      stat: '50K+ alerts daily',
      useCases: [
        { title: 'Business Owners', desc: 'Plan operations around power availability' },
        { title: 'Remote Workers', desc: 'Schedule calls during stable power' },
        { title: 'Home Owners', desc: 'Protect appliances and preserve food' }
      ]
    },
    {
      icon: Users,
      title: 'Digital Ajo',
      subtitle: 'Trusted Savings Circles',
      description: 'Join verified savings groups (Esusu) with community members. Build wealth together with transparency and trust.',
      longDescription: 'Traditional Ajo meets modern technology. Create or join verified savings circles with friends, family, or trusted community members. Every contribution is tracked automatically, payouts are scheduled transparently, and disputes are resolved fairly. Build wealth collectively while maintaining the cultural tradition.',
      gradient: 'from-green-500 via-emerald-500 to-teal-600',
      benefits: [
        'Verified member backgrounds',
        'Automatic contribution tracking',
        'Secure escrow payments',
        'Transparent payout schedules',
        'Dispute resolution system',
        'Multiple circle participation'
      ],
      image: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?auto=format&fit=crop&q=80&w=1000',
      stat: '₦2.1B+ saved',
      useCases: [
        { title: 'Small Business', desc: 'Pool capital for inventory' },
        { title: 'Young Professionals', desc: 'Save for major purchases' },
        { title: 'Community Groups', desc: 'Build collective wealth' }
      ]
    },
    {
      icon: ShoppingBag,
      title: 'Market Basket',
      subtitle: 'Price Intelligence',
      description: 'Compare prices across markets instantly. Find the best deals on rice, tomatoes, and essentials before you shop.',
      longDescription: 'Know exactly what you should pay before stepping into any market. Compare prices for rice, beans, tomatoes, onions, chicken, and 100+ commodities across different markets in real-time. Set price alerts for your favorite items, track inflation trends, and find the best deals near you. Never overpay again.',
      gradient: 'from-purple-500 via-pink-500 to-rose-600',
      benefits: [
        'Real-time price comparison',
        'Deal alerts for favorites',
        'Historical price trends',
        'Inflation tracking',
        'Market-specific pricing',
        'Shopping list optimization'
      ],
      image: 'https://images.unsplash.com/photo-1583258292688-d0213dc5a3a8?auto=format&fit=crop&q=80&w=1000',
      stat: 'Save 30% average',
      useCases: [
        { title: 'Families', desc: 'Reduce grocery bills significantly' },
        { title: 'Restaurant Owners', desc: 'Find wholesale deals' },
        { title: 'Event Planners', desc: 'Budget accurately for catering' }
      ]
    },
    {
      icon: Mic,
      title: 'Voice Reports',
      subtitle: 'Hands-Free Reporting',
      description: 'Report incidents while driving with voice messages. Your voice becomes actionable intel for the community.',
      longDescription: 'Safety first - report without taking your hands off the wheel. Simply tap and speak to report traffic, accidents, fuel availability, or any community update. Our AI instantly transcribes, tags your location, and shares with nearby users. Your 10-second voice note could save someone hours.',
      gradient: 'from-blue-500 via-cyan-500 to-sky-600',
      benefits: [
        'Voice-to-text AI transcription',
        'Auto-location tagging',
        'Instant community sharing',
        'Hands-free safety',
        'Multi-language support',
        'Audio playback option'
      ],
      image: 'https://images.unsplash.com/photo-1589903308904-1010c2294adc?auto=format&fit=crop&q=80&w=1000',
      stat: '10K+ voice notes/day',
      useCases: [
        { title: 'Drivers', desc: 'Report while on the move' },
        { title: 'Commuters', desc: 'Share real-time updates' },
        { title: 'Delivery Riders', desc: 'Alert others to hazards' }
      ]
    },
    {
      icon: Fuel,
      title: 'Fuel Watch',
      subtitle: 'Beat Scarcity',
      description: 'Locate available fuel stations with current prices and queue status. Stop wasting time and fuel searching.',
      longDescription: 'Find fuel in seconds, not hours. See which stations have fuel right now, current prices, queue length, and estimated wait time. Filter by fuel type (petrol, diesel, gas), distance, and price. Get alerts when fuel arrives at your favorite stations. Never waste fuel searching for fuel again.',
      gradient: 'from-red-500 via-orange-500 to-amber-600',
      benefits: [
        'Live fuel availability',
        'Real-time price tracking',
        'Queue length & wait times',
        'Multi-fuel type support',
        'Arrival alerts',
        'Route optimization'
      ],
      image: 'https://images.unsplash.com/photo-1545158535-c3f7168c28b6?auto=format&fit=crop&q=80&w=1000',
      stat: '5K+ stations tracked',
      useCases: [
        { title: 'Daily Commuters', desc: 'Plan refueling efficiently' },
        { title: 'Logistics Companies', desc: 'Optimize fleet fuel costs' },
        { title: 'Generators', desc: 'Find diesel during shortages' }
      ]
    },
    {
      icon: Radio,
      title: 'Swap & Shop',
      subtitle: 'Neighborhood Marketplace',
      description: 'Trade items safely with verified neighbors. From furniture to electronics, buy and sell locally.',
      longDescription: 'Your neighborhood marketplace with safety built-in. Buy and sell everything from furniture to phones with verified local sellers. Meet in safe locations, check seller ratings, and communicate securely in-app. Unlike other platforms, we verify users and locations, making transactions safer for everyone.',
      gradient: 'from-indigo-500 via-purple-500 to-violet-600',
      benefits: [
        'Verified seller profiles',
        'Location-based listings',
        'Safe meeting spots',
        'In-app messaging',
        'Rating system',
        'Photo verification'
      ],
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&q=80&w=1000',
      stat: '50K+ items listed',
      useCases: [
        { title: 'Students', desc: 'Affordable second-hand items' },
        { title: 'Families', desc: 'Sell outgrown items locally' },
        { title: 'Entrepreneurs', desc: 'Local customer base' }
      ]
    },
    {
      icon: Shield,
      title: 'SafePay Escrow',
      subtitle: 'Protected Transactions',
      description: 'Buy and sell with confidence. Money held securely until both parties are satisfied. Zero scam risk.',
      longDescription: 'Nigeria\'s most trusted payment protection system. When you buy, money is held in escrow until you confirm receipt and satisfaction. Sellers get paid guaranteed once delivery is confirmed. If there\'s an issue, our resolution team steps in. With 99.9% transaction success rate, SafePay has processed over ₦2 billion in safe transactions.',
      gradient: 'from-emerald-500 via-green-500 to-lime-600',
      benefits: [
        '100% buyer protection',
        'Seller payment guarantee',
        'Dispute resolution team',
        'Instant refund processing',
        'Transaction insurance',
        'Bank-level encryption'
      ],
      image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?auto=format&fit=crop&q=80&w=1000',
      stat: '99.9% success rate',
      useCases: [
        { title: 'Online Shoppers', desc: 'Shop fearlessly from anywhere' },
        { title: 'Small Businesses', desc: 'Build customer trust' },
        { title: 'Freelancers', desc: 'Secure project payments' }
      ]
    },
    {
      icon: Eye,
      title: 'Crowd Watch',
      subtitle: 'Bus Stop Intelligence',
      description: 'Check real-time crowd levels at bus stops before you leave. Avoid crushing and save time.',
      longDescription: 'Beat the morning rush with real-time crowd intelligence. See exactly how crowded bus stops are right now, predicted wait times, and optimal departure times. Users report crowd levels in real-time, helping thousands plan their commutes better. Integrated with traffic data for complete journey planning.',
      gradient: 'from-cyan-500 via-blue-500 to-indigo-600',
      benefits: [
        'Live crowd levels',
        'Wait time predictions',
        'Best departure times',
        'Alternative routes',
        'Bus arrival tracking',
        'Safety ratings'
      ],
      image: 'https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?auto=format&fit=crop&q=80&w=1000',
      stat: '1K+ stops monitored',
      useCases: [
        { title: 'Office Workers', desc: 'Time commute perfectly' },
        { title: 'Students', desc: 'Avoid morning crushing' },
        { title: 'Parents', desc: 'Safe school runs' }
      ]
    }
  ];

  const stats = [
    { value: '10,045', label: 'Active Users', icon: Users, suffix: '+', color: 'text-green-400', info: 'Growing by 500+ daily' },
    { value: '37', label: 'States Covered', icon: MapPin, suffix: '', color: 'text-blue-400', info: 'All 36 states + FCT' },
    { value: '50', label: 'Reports Per Minute', icon: TrendingUp, suffix: '+', color: 'text-purple-400', info: '72,000+ daily reports' },
    { value: '2.1', label: 'Billion Naira Saved', icon: DollarSign, suffix: 'B', color: 'text-yellow-400', info: 'Through Ajo & SafePay' },
    { value: '99.9', label: 'Uptime', icon: CheckCircle, suffix: '%', color: 'text-emerald-400', info: 'Enterprise-grade reliability' },
    { value: '4.9', label: 'User Rating', icon: Star, suffix: '/5', color: 'text-orange-400', info: 'From 2,000+ reviews' }
  ];

  const howItWorks = [
    {
      step: '1',
      title: 'Download & Sign Up',
      description: 'Create your free account in 30 seconds. No credit card required.',
      icon: Smartphone,
      color: 'from-green-500 to-emerald-600'
    },
    {
      step: '2',
      title: 'Enable Location',
      description: 'Allow GPS access to see hyper-local updates from your neighborhood.',
      icon: MapPin,
      color: 'from-blue-500 to-cyan-600'
    },
    {
      step: '3',
      title: 'Start Reporting',
      description: 'Share what you see - traffic, power, fuel, prices. Help your community.',
      icon: MessageSquare,
      color: 'from-purple-500 to-pink-600'
    },
    {
      step: '4',
      title: 'Get Verified Intel',
      description: 'Receive real-time, verified updates from thousands of users near you.',
      icon: CheckCircle,
      color: 'from-orange-500 to-red-600'
    }
  ];

  const successStories = [
    {
      metric: '3.5 Hours',
      description: 'Average time saved per week',
      icon: Clock,
      color: 'text-blue-400'
    },
    {
      metric: '₦25,000',
      description: 'Average monthly savings',
      icon: TrendingUp,
      color: 'text-green-400'
    },
    {
      metric: '47%',
      description: 'Reduction in fuel search time',
      icon: Fuel,
      color: 'text-orange-400'
    },
    {
      metric: '89%',
      description: 'Users avoid traffic daily',
      icon: Car,
      color: 'text-purple-400'
    }
  ];

  const testimonials = [
    {
      name: 'Chiamaka Okonkwo',
      role: 'Small Business Owner',
      location: 'Lekki, Lagos',
      text: 'Sentlo is a game-changer! I saved ₦50,000 last month by tracking market prices. The Light Watch feature helps me plan when to use my generator. Absolute lifesaver!',
      avatar: '👩🏾‍💼',
      rating: 5,
      verified: true,
      date: 'Feb 2026',
      savings: '₦50,000/month'
    },
    {
      name: 'Tunde Bakare',
      role: 'Uber Driver',
      location: 'Ikeja, Lagos',
      text: 'The traffic reports are incredibly accurate. I avoid go-slows and make more trips daily. The Fuel Watch saved me 3 hours of searching yesterday. Worth every second!',
      avatar: '👨🏿',
      rating: 5,
      verified: true,
      date: 'Feb 2026',
      savings: '3hrs/day'
    },
    {
      name: 'Fatima Ibrahim',
      role: 'IT Professional',
      location: 'Abuja',
      text: 'My Digital Ajo circle has 15 members. We\'ve saved over ₦2 million together! The SafePay escrow gives me peace of mind when buying online. No more fear of scammers!',
      avatar: '👩🏾‍💻',
      rating: 5,
      verified: true,
      date: 'Jan 2026',
      savings: '₦2M+ saved'
    },
    {
      name: 'Emeka Nwankwo',
      role: 'Engineer',
      location: 'Port Harcourt',
      text: 'Finally, a Nigerian app that understands our daily struggles! The community is helpful and reports are verified. This is what we\'ve been waiting for!',
      avatar: '👨🏿‍🔧',
      rating: 5,
      verified: true,
      date: 'Jan 2026',
      savings: 'Priceless'
    },
    {
      name: 'Blessing Adeyemi',
      role: 'Teacher',
      location: 'Ibadan',
      text: 'The voice reporting feature is perfect for me. I report bad roads on my way to school. Seeing the community respond and authorities take action is incredible!',
      avatar: '👩🏾‍🏫',
      rating: 5,
      verified: true,
      date: 'Feb 2026',
      savings: 'Daily impact'
    },
    {
      name: 'Ahmed Musa',
      role: 'Business Analyst',
      location: 'Kano',
      text: 'Market Basket helps me track commodity prices. I know exactly when to stock up. My family saves at least 25% on groceries now. Highly recommend!',
      avatar: '👨🏾‍💼',
      rating: 5,
      verified: true,
      date: 'Jan 2026',
      savings: '25% groceries'
    }
  ];

  const problems = [
    {
      icon: AlertTriangle,
      title: 'Stuck in Traffic',
      description: 'Waste hours daily in Lagos go-slow with no alternative routes',
      impact: '4.5 hours wasted daily',
      cost: '₦15,000 fuel monthly',
      color: 'text-red-500'
    },
    {
      icon: Zap,
      title: 'Constant Blackouts',
      description: 'NEPA strikes without warning, spoiling food and disrupting work',
      impact: '₦30,000 spoilage',
      cost: '₦50,000 generator fuel',
      color: 'text-yellow-500'
    },
    {
      icon: Fuel,
      title: 'Fuel Scarcity',
      description: 'Drive around for hours searching for petrol, wasting time and money',
      impact: '3 hours searching',
      cost: '₦10,000 wasted fuel',
      color: 'text-orange-500'
    },
    {
      icon: TrendingUp,
      title: 'Price Exploitation',
      description: 'Overpay for commodities because you don\'t know the real market prices',
      impact: '30% overpayment',
      cost: '₦40,000 monthly',
      color: 'text-purple-500'
    },
    {
      icon: AlertTriangle,
      title: 'Online Scams',
      description: 'Fear buying online due to countless 419 scammers and fraudsters',
      impact: 'Trust issues',
      cost: 'Missed opportunities',
      color: 'text-red-500'
    },
    {
      icon: Clock,
      title: 'Wasted Time',
      description: 'No reliable information means poor planning and lost opportunities',
      impact: '20+ hrs/week',
      cost: 'Opportunity cost',
      color: 'text-blue-500'
    }
  ];

  const pricingPlans = [
    {
      name: 'Free',
      price: '₦0',
      period: 'forever',
      description: 'Perfect for getting started',
      popular: false,
      gradient: 'from-gray-700 to-gray-900',
      features: [
        { text: 'Basic reports access', included: true },
        { text: 'Community updates', included: true },
        { text: 'Light Watch alerts', included: true },
        { text: 'Market price tracking', included: true },
        { text: 'Voice reports (5/day)', included: true },
        { text: 'Fuel station finder', included: true },
        { text: 'Traffic updates', included: true },
        { text: 'Priority support', included: false },
        { text: 'Advanced analytics', included: false },
        { text: 'SafePay escrow', included: false },
        { text: 'Digital Ajo circles', included: false },
        { text: 'Unlimited voice reports', included: false }
      ],
      cta: 'Get Started Free',
      savings: null
    },
    {
      name: 'Pro',
      price: '₦2,500',
      period: 'per month',
      description: 'For power users who need more',
      popular: true,
      gradient: 'from-green-600 to-emerald-700',
      features: [
        { text: 'Everything in Free', included: true },
        { text: 'Unlimited voice reports', included: true },
        { text: 'Priority notifications', included: true },
        { text: 'Advanced filters & search', included: true },
        { text: 'SafePay escrow (5 free/month)', included: true },
        { text: 'Historical data access', included: true },
        { text: 'Digital Ajo participation', included: true },
        { text: 'Price alerts & tracking', included: true },
        { text: 'Ad-free experience', included: true },
        { text: '24/7 priority support', included: true },
        { text: 'Custom notifications', included: true },
        { text: 'Export reports', included: true }
      ],
      cta: 'Start 7-Day Free Trial',
      savings: 'Save ₦25,000/month'
    },
    {
      name: 'Business',
      price: '₦15,000',
      period: 'per month',
      description: 'For businesses and organizations',
      popular: false,
      gradient: 'from-purple-600 to-indigo-700',
      features: [
        { text: 'Everything in Pro', included: true },
        { text: 'Unlimited SafePay transactions', included: true },
        { text: 'Business analytics dashboard', included: true },
        { text: 'API access for integration', included: true },
        { text: 'White-label options', included: true },
        { text: 'Dedicated account manager', included: true },
        { text: 'Custom integrations', included: true },
        { text: 'Team collaboration tools', included: true },
        { text: 'Training & onboarding', included: true },
        { text: 'Multi-location management', included: true },
        { text: 'Advanced reporting', included: true },
        { text: 'SLA guarantees', included: true }
      ],
      cta: 'Contact Sales',
      savings: 'Save ₦200,000/month'
    }
  ];

  const faqs = [
    {
      question: 'How does Sentlo verify reports to ensure accuracy?',
      answer: 'Reports are verified through multiple layers: 1) Community consensus - multiple users reporting the same thing increases trust, 2) Trust scores - users earn reputation through accurate reporting history, 3) Location verification - GPS timestamps confirm reporters are actually there, 4) Official accounts - verified agencies like LASTMA, FRSC, and NEPA provide authoritative updates, 5) AI filtering - machine learning detects spam and fake reports. Reports with higher verification show trust badges.'
    },
    {
      question: 'Is my data safe and private? What about my location?',
      answer: 'Your privacy is our top priority. We use military-grade AES-256 encryption for all data transmission and storage. Your location is ONLY shared when you actively choose to create a report - never tracked in the background. You can report anonymously anytime. We never sell your data to third parties, advertisers, or any external entity. All our systems comply with NDPR (Nigeria Data Protection Regulation). You can delete your account and all data anytime with one click.'
    },
    {
      question: 'How does SafePay escrow work? Is it really safe?',
      answer: 'SafePay is Nigeria\'s most trusted payment protection: 1) When you buy, money is held in our secure escrow account (regulated by CBN), 2) Seller ships item and provides tracking, 3) You receive and inspect the item, 4) If satisfied, you confirm and we release payment to seller, 5) If there\'s an issue, our dispute team investigates using evidence from both parties, 6) Refunds are processed within 24 hours if buyer is right. We\'ve successfully processed ₦2.1 billion with 99.9% success rate. Funds are insured and held in licensed financial institutions.'
    },
    {
      question: 'Can I use Sentlo outside Lagos and Abuja?',
      answer: 'Absolutely! Sentlo covers all 36 states in Nigeria plus FCT Abuja - that\'s 37 locations total. We have active, thriving communities in Lagos (4,521 users), Abuja (2,134 users), Port Harcourt (987 users), Kano (756 users), Ibadan (645 users), Enugu (423 users), Kaduna (398 users), Benin City (312 users), and 29 other states. Even if your area has fewer users now, every report you make helps build the community. We\'re adding new features specifically for tier-2 and tier-3 cities monthly.'
    },
    {
      question: 'What are Digital Ajo circles and how do they work?',
      answer: 'Digital Ajo (also called Esusu or thrift) brings the traditional savings model online with complete transparency and security: 1) Create or join a circle (5-50 members), 2) Set contribution amount and schedule (weekly, bi-weekly, monthly), 3) Each cycle, members contribute and one person receives the full pot, 4) Rotation continues until everyone has received, 5) All transactions are automated and tracked in-app, 6) Defaulters are flagged and banned from future circles, 7) SafePay escrow holds contributions securely. Many Nigerians are using Ajo circles to save for business capital, rent, education, or major purchases. Our largest circle has saved ₦15 million collectively!'
    },
    {
      question: 'Is there really a completely free plan? What\'s the catch?',
      answer: 'Yes, Sentlo is FREE FOREVER for core features - no credit card, no trial period, no hidden fees. You get: unlimited access to reports, Light Watch alerts, market prices, fuel finder, traffic updates, and 5 voice reports daily. We can offer this because: 1) Pro users (₦2,500/month) get advanced features and unlimited usage, 2) Businesses pay for API access and analytics, 3) We charge small fees on SafePay escrow transactions (2%), 4) Premium Ajo circles have optional features. We believe access to community information should be free for all Nigerians. You can use Sentlo free forever and still get massive value!'
    },
    {
      question: 'How accurate are the traffic reports? Can I rely on them for daily commute?',
      answer: 'Our traffic reports are incredibly accurate - here\'s why: 1) Real-time crowdsourcing from 10,000+ active users driving right now, 2) Average report freshness is 3-5 minutes (we show timestamps), 3) Machine learning validates reports against historical patterns, 4) Integration with Google Maps traffic data for verification, 5) Trust-scored users (experienced reporters) carry more weight, 6) Route suggestions show multiple users\' experiences. Over 89% of Pro users say they avoid traffic jams daily. Reports are most accurate in Lagos, Abuja, and Port Harcourt where we have highest user density. The more people use it in your area, the better it gets!'
    },
    {
      question: 'Can businesses or market vendors advertise prices on Sentlo?',
      answer: 'Yes! We have special programs for businesses: 1) Fuel Stations - Advertise availability, prices, and queue status to reach thousands of nearby drivers (₦5,000/month), 2) Markets & Vendors - Showcase daily prices and special deals to bargain hunters (₦3,000/month), 3) Restaurants & Eateries - Promote menu, prices, and availability (₦4,000/month), 4) Service Providers - Reach local customers actively looking (₦6,000/month). Business accounts get analytics showing views, engagement, and conversions. Many fuel stations see 50+ additional customers daily. Many markets report 30% increase in shoppers. Contact our business team for custom packages and bulk discounts.'
    },
    {
      question: 'What languages does Sentlo support? Can I report in Yoruba, Igbo, or Hausa?',
      answer: 'Currently, Sentlo supports English as the primary language for maximum reach across Nigeria. However, we\'re actively developing support for: Yoruba, Igbo, Hausa, and Pidgin English - launching Q2 2026! Voice reports work in any language and are auto-transcribed. You can write reports in any Nigerian language now, and other users in your community will understand. Our AI is being trained on Nigerian languages to provide auto-translation. We believe language should never be a barrier to community information!'
    },
    {
      question: 'How does Sentlo make money if the basic plan is free?',
      answer: 'Great question! We believe in transparency. Our revenue comes from: 1) Pro subscriptions (₦2,500/month) - 18% of users upgrade for unlimited features, 2) Business subscriptions (₦15,000/month) - companies get analytics and API access, 3) SafePay transaction fees (2%) - small fee on escrow payments for security infrastructure, 4) Premium Ajo features - optional paid features for savings circles, 5) Business advertising - verified businesses can promote to local users, 6) API licensing - larger companies pay for data integration. We\'re venture-backed and focused on growth over short-term profits. Free users are never shown intrusive ads, and we NEVER sell your data!'
    }
  ];

  const comparisons = [
    {
      feature: 'Real-time Traffic',
      sentlo: true,
      google: true,
      others: false,
      sentloDetails: 'Community-verified with alternative routes'
    },
    {
      feature: 'Power Outage Tracking',
      sentlo: true,
      google: false,
      others: false,
      sentloDetails: 'First in Nigeria'
    },
    {
      feature: 'Fuel Availability',
      sentlo: true,
      google: false,
      others: false,
      sentloDetails: 'Live prices & queue status'
    },
    {
      feature: 'Market Prices',
      sentlo: true,
      google: false,
      others: false,
      sentloDetails: '100+ commodities tracked'
    },
    {
      feature: 'Payment Protection',
      sentlo: true,
      google: false,
      others: false,
      sentloDetails: '₦2B+ safely processed'
    },
    {
      feature: 'Digital Ajo/Esusu',
      sentlo: true,
      google: false,
      others: false,
      sentloDetails: 'Traditional savings, modern tech'
    },
    {
      feature: 'Voice Reporting',
      sentlo: true,
      google: false,
      others: false,
      sentloDetails: 'Hands-free, while driving'
    },
    {
      feature: 'Nigerian Focus',
      sentlo: true,
      google: false,
      others: false,
      sentloDetails: 'Built for Naija by Naija'
    }
  ];

  // Auto-rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Email submitted:', email);
    setEmail('');
    alert('🎉 Thank you! We\'ll keep you updated with exciting news and features!');
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      {/* Fixed Navigation */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-white/10"
      >
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-green-400" />
            <span className="text-2xl font-black bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">SENTLO</span>
            <span className="hidden sm:inline px-3 py-1 bg-green-500 rounded-full text-xs font-bold animate-pulse">LIVE 🇳🇬</span>
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-6">
            <a href="#features" className="hover:text-green-400 transition-colors font-medium">Features</a>
            <a href="#how-it-works" className="hover:text-green-400 transition-colors font-medium">How It Works</a>
            <a href="#pricing" className="hover:text-green-400 transition-colors font-medium">Pricing</a>
            <a href="#testimonials" className="hover:text-green-400 transition-colors font-medium">Reviews</a>
            <a href="#faq" className="hover:text-green-400 transition-colors font-medium">FAQ</a>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/app')}
              className="px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl font-bold hover:shadow-lg hover:shadow-green-500/50 transition-all hover:scale-105"
            >
              Launch App →
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 hover:bg-white/10 rounded-lg"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="md:hidden border-t border-white/10 bg-black/95"
            >
              <div className="px-4 py-4 flex flex-col gap-3">
                <a href="#features" onClick={() => setMobileMenuOpen(false)} className="py-2 hover:text-green-400 transition-colors">Features</a>
                <a href="#how-it-works" onClick={() => setMobileMenuOpen(false)} className="py-2 hover:text-green-400 transition-colors">How It Works</a>
                <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="py-2 hover:text-green-400 transition-colors">Pricing</a>
                <a href="#testimonials" onClick={() => setMobileMenuOpen(false)} className="py-2 hover:text-green-400 transition-colors">Reviews</a>
                <a href="#faq" onClick={() => setMobileMenuOpen(false)} className="py-2 hover:text-green-400 transition-colors">FAQ</a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center pt-20 px-4 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-green-900/30 via-black to-black" />
          <motion.div
            style={{ y: y1 }}
            className="absolute top-20 left-10 w-96 h-96 bg-green-500/20 rounded-full blur-3xl"
            animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.3, 0.2] }}
            transition={{ duration: 8, repeat: Infinity }}
          />
          <motion.div
            style={{ y: y2 }}
            className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-3xl"
            animate={{ scale: [1, 1.3, 1], opacity: [0.1, 0.2, 0.1] }}
            transition={{ duration: 10, repeat: Infinity }}
          />
          
          {/* Animated Grid */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(74,222,128,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(74,222,128,0.03)_1px,transparent_1px)] bg-[size:64px_64px]" />
          
          {/* Floating Particles */}
          {[...Array(40)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-green-400 rounded-full"
              initial={{ 
                x: Math.random() * window.innerWidth, 
                y: Math.random() * window.innerHeight,
                opacity: Math.random() * 0.7
              }}
              animate={{ 
                y: [null, Math.random() * window.innerHeight],
                opacity: [null, 0, Math.random() * 0.7]
              }}
              transition={{ 
                duration: Math.random() * 10 + 10, 
                repeat: Infinity,
                ease: 'linear'
              }}
            />
          ))}
        </div>

        <motion.div
          style={{ opacity, scale }}
          className="relative z-10 max-w-6xl mx-auto text-center"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-full mb-8 backdrop-blur-xl"
          >
            <Activity className="w-4 h-4 text-green-400 animate-pulse" />
            <span className="text-sm font-bold text-green-400">10,045+ Nigerians Online Now • 50+ Reports/Min</span>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black mb-6 leading-tight"
          >
            Your City's{' '}
            <span className="relative inline-block">
              <span className="bg-gradient-to-r from-green-400 via-emerald-400 to-cyan-400 bg-clip-text text-transparent animate-pulse">
                Real-Time
              </span>
              <motion.div
                className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-green-400 to-emerald-400 rounded-full"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.8, duration: 0.8 }}
              />
            </span>
            <br />
            Intelligence Network
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-xl md:text-2xl text-gray-400 mb-8 max-w-4xl mx-auto leading-relaxed"
          >
            <span className="text-white font-bold">Traffic? Power outages? Fuel scarcity? Market prices?</span>
            <br />
            Get <span className="text-green-400 font-bold">instant, verified answers</span> from 10,000+ Nigerians in your neighborhood.
          </motion.p>

          {/* Value Props */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex flex-wrap items-center justify-center gap-6 mb-12 text-sm"
          >
            <div className="flex items-center gap-2 bg-white/5 backdrop-blur-xl px-4 py-2 rounded-full">
              <Clock className="w-5 h-5 text-blue-400" />
              <span><span className="font-bold text-white">Save 3.5 hrs/week</span></span>
            </div>
            <div className="flex items-center gap-2 bg-white/5 backdrop-blur-xl px-4 py-2 rounded-full">
              <DollarSign className="w-5 h-5 text-green-400" />
              <span><span className="font-bold text-white">Save ₦25K/month</span></span>
            </div>
            <div className="flex items-center gap-2 bg-white/5 backdrop-blur-xl px-4 py-2 rounded-full">
              <Users className="w-5 h-5 text-purple-400" />
              <span><span className="font-bold text-white">Join 10K+ users</span></span>
            </div>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-8"
          >
            <button
              onClick={() => navigate('/app')}
              className="group px-10 py-5 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl font-black text-xl shadow-2xl shadow-green-500/50 hover:shadow-green-500/80 hover:scale-105 transition-all flex items-center gap-3 justify-center"
            >
              <Sparkles className="w-6 h-6" />
              <span>Start Free Today</span>
              <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
            </button>
            
            <button
              onClick={() => setIsVideoPlaying(true)}
              className="px-10 py-5 bg-white/5 backdrop-blur-xl rounded-2xl font-bold text-xl border-2 border-white/20 hover:bg-white/10 hover:scale-105 transition-all flex items-center gap-3 justify-center"
            >
              <Play className="w-6 h-6" />
              <span>Watch Demo (2 min)</span>
            </button>
          </motion.div>

          {/* Trust Badges */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
            className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500"
          >
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span>Free Forever Plan</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-400" />
              <span>Bank-Level Security</span>
            </div>
            <div className="flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-green-400" />
              <span>No Credit Card Needed</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-green-400" />
              <span>4.9/5 Rating</span>
            </div>
          </motion.div>

          {/* Scroll Indicator */}
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute bottom-10 left-1/2 -translate-x-1/2"
          >
            <div className="flex flex-col items-center gap-2">
              <span className="text-xs text-gray-500">Scroll to explore</span>
              <ChevronDown className="w-8 h-8 text-white/50" />
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Live Activity Feed */}
      <section className="relative py-12 px-4 bg-gradient-to-b from-black to-green-950/10 border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Activity className="w-6 h-6 text-green-400 animate-pulse" />
            <h3 className="text-2xl font-black">Live Right Now in Nigeria</h3>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4">
            <AnimatePresence mode="popLayout">
              {liveReports.slice(0, 3).map((report) => (
                <motion.div
                  key={report.id}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  className="bg-white/5 backdrop-blur-xl rounded-xl p-4 border border-white/10 hover:border-green-500/30 transition-all"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <report.icon className={`w-5 h-5 ${report.color} flex-shrink-0 mt-0.5`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-300 leading-relaxed">{report.text}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3 h-3" />
                      <span>{report.location}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-green-400">{report.time}</span>
                      <div className="flex items-center gap-1">
                        <ThumbsUp className="w-3 h-3" />
                        <span>{report.upvotes}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
          
          <div className="text-center mt-6">
            <button
              onClick={() => navigate('/app')}
              className="text-green-400 hover:text-green-300 font-bold text-sm flex items-center gap-2 mx-auto"
            >
              <span>See all live updates</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Stats Counter Section */}
      <section ref={statsRef} className="relative py-24 px-4 bg-gradient-to-b from-green-950/10 via-black to-black">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black mb-4">
              Trusted by Thousands <span className="text-green-400">Daily</span>
            </h2>
            <p className="text-xl text-gray-400">Real numbers, real impact, real people</p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-16">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={isStatsInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-green-500/50 transition-all group cursor-pointer"
              >
                <stat.icon className={`w-8 h-8 mb-3 ${stat.color} group-hover:scale-110 transition-transform`} />
                <div className="text-4xl font-black mb-2">
                  {isStatsInView && (
                    <CountUp end={parseFloat(stat.value)} suffix={stat.suffix} />
                  )}
                </div>
                <div className="text-xs text-gray-500 uppercase font-bold mb-2">{stat.label}</div>
                <div className="text-xs text-gray-600">{stat.info}</div>
              </motion.div>
            ))}
          </div>

          {/* Success Stories */}
          <div className="grid md:grid-cols-4 gap-6">
            {successStories.map((story, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <story.icon className={`w-12 h-12 mx-auto mb-4 ${story.color}`} />
                <div className="text-3xl font-black mb-2">{story.metric}</div>
                <div className="text-sm text-gray-400">{story.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="relative py-24 px-4 bg-gradient-to-b from-black to-red-950/20">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-6xl font-black mb-4">
              Every Nigerian Faces{' '}
              <span className="text-red-500">These Challenges</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              The daily struggle is real. But you don't have to suffer alone anymore.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {problems.map((problem, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-red-500/50 transition-all"
              >
                <problem.icon className={`w-12 h-12 mb-4 ${problem.color}`} />
                <h3 className="text-xl font-black mb-2">{problem.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">{problem.description}</p>
                <div className="space-y-1 text-xs">
                  <div className="flex items-center gap-2 text-red-400">
                    <TrendingDown className="w-4 h-4" />
                    <span className="font-bold">{problem.impact}</span>
                  </div>
                  <div className="flex items-center gap-2 text-orange-400">
                    <DollarSign className="w-4 h-4" />
                    <span className="font-bold">{problem.cost}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <div className="inline-block px-8 py-6 bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-2xl backdrop-blur-xl">
              <p className="text-2xl font-black text-red-400 mb-2">
                These problems cost Nigerians ₦billions and 1,000+ hours annually
              </p>
              <p className="text-gray-400 mb-4">But there's a better way...</p>
              <button
                onClick={() => navigate('/app')}
                className="px-6 py-3 bg-green-500 hover:bg-green-600 rounded-xl font-bold transition-all"
              >
                See The Solution →
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="relative py-24 px-4 bg-gradient-to-b from-black via-blue-950/10 to-black">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-6xl font-black mb-4">
              Get Started in <span className="text-green-400">30 Seconds</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              It's incredibly simple. No complicated setup, no learning curve.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {howItWorks.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="relative"
              >
                {/* Connector Line */}
                {index < howItWorks.length - 1 && (
                  <div className="hidden md:block absolute top-16 left-full w-full h-0.5 bg-gradient-to-r from-green-500/50 to-transparent" />
                )}
                
                <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-green-500/50 transition-all">
                  {/* Step Number */}
                  <div className={`absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-r ${step.color} rounded-xl flex items-center justify-center font-black text-2xl shadow-lg`}>
                    {step.step}
                  </div>
                  
                  <step.icon className="w-16 h-16 text-green-400 mb-4 mt-4" />
                  <h3 className="text-xl font-black mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <button
              onClick={() => navigate('/app')}
              className="px-10 py-5 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl font-black text-xl shadow-2xl shadow-green-500/50 hover:shadow-green-500/80 hover:scale-105 transition-all"
            >
              Start Your Free Account Now →
            </button>
          </motion.div>
        </div>
      </section>

      {/* Coverage Map */}
      <section className="relative py-24 px-4 bg-gradient-to-b from-black to-purple-950/10">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-6xl font-black mb-4">
              Active in <span className="text-purple-400">All 37 States</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              From Lagos to Maiduguri, from Port Harcourt to Sokoto - we've got Nigeria covered
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-4 mb-12">
            {nigerianStates.map((state, index) => (
              <motion.button
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                onClick={() => setActiveState(state.name)}
                className={`p-4 rounded-xl border transition-all ${
                  activeState === state.name
                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 border-purple-500 shadow-lg shadow-purple-500/50'
                    : 'bg-white/5 border-white/10 hover:border-purple-500/50'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold">{state.name}</h3>
                  {state.active && (
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  )}
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-400">
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    <span>{state.users}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Activity className="w-4 h-4" />
                    <span>{state.reports}</span>
                  </div>
                </div>
              </motion.button>
            ))}
          </div>

          <div className="text-center bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10">
            <MapPin className="w-12 h-12 text-purple-400 mx-auto mb-4" />
            <h3 className="text-2xl font-black mb-2">Your State Not Listed?</h3>
            <p className="text-gray-400 mb-4">
              We're expanding rapidly! Be the first in your area and earn founding member status.
            </p>
            <button
              onClick={() => navigate('/app')}
              className="px-6 py-3 bg-purple-500 hover:bg-purple-600 rounded-xl font-bold transition-all"
            >
              Start Your Community →
            </button>
          </div>
        </div>
      </section>

      {/* Features Showcase */}
      <section id="features" className="relative py-24 px-4 bg-gradient-to-b from-black via-green-950/20 to-black">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-6xl font-black mb-4">
              8 Powerful Features
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Everything you need to navigate Nigeria's cities with confidence and ease
            </p>
          </motion.div>

          {/* Feature Tabs */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {features.map((feature, index) => (
              <button
                key={index}
                onClick={() => setActiveFeature(index)}
                className={`px-6 py-3 rounded-xl font-bold transition-all ${
                  activeFeature === index
                    ? `bg-gradient-to-r ${feature.gradient} text-white shadow-lg scale-105`
                    : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
              >
                <feature.icon className="w-5 h-5 inline-block mr-2" />
                {feature.title}
              </button>
            ))}
          </div>

          {/* Active Feature Display */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeFeature}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.3 }}
              className="bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-3xl overflow-hidden border border-white/10"
            >
              <div className="grid md:grid-cols-2 gap-8 p-8 md:p-12">
                {/* Content */}
                <div className="flex flex-col justify-center">
                  <div className={`inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r ${features[activeFeature].gradient} rounded-full w-fit mb-6`}>
                    {(() => {
                      const IconComponent = features[activeFeature].icon;
                      return <IconComponent className="w-5 h-5" />;
                    })()}
                    <span className="font-bold text-sm">{features[activeFeature].subtitle}</span>
                  </div>
                  
                  <h3 className="text-4xl md:text-5xl font-black mb-4">
                    {features[activeFeature].title}
                  </h3>
                  
                  <p className="text-xl text-gray-400 mb-6 leading-relaxed">
                    {features[activeFeature].description}
                  </p>

                  <p className="text-sm text-gray-500 mb-8 leading-relaxed">
                    {features[activeFeature].longDescription}
                  </p>

                  <div className="grid grid-cols-2 gap-3 mb-8">
                    {features[activeFeature].benefits.map((benefit, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-300">{benefit}</span>
                      </div>
                    ))}
                  </div>

                  {/* Use Cases */}
                  <div className="bg-white/5 rounded-2xl p-6 mb-8">
                    <h4 className="text-lg font-bold mb-4">Perfect For:</h4>
                    <div className="space-y-3">
                      {features[activeFeature].useCases.map((useCase, i) => (
                        <div key={i}>
                          <div className="font-bold text-green-400 text-sm">{useCase.title}</div>
                          <div className="text-xs text-gray-500">{useCase.desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <button
                      onClick={() => navigate('/app')}
                      className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl font-bold hover:shadow-lg hover:shadow-green-500/50 transition-all hover:scale-105"
                    >
                      Try It Free →
                    </button>
                    <div>
                      <div className="text-3xl font-black text-green-400">
                        {features[activeFeature].stat}
                      </div>
                      <div className="text-xs text-gray-500">and growing fast</div>
                    </div>
                  </div>
                </div>

                {/* Image */}
                <div className="relative">
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5 }}
                    className="relative rounded-2xl overflow-hidden shadow-2xl"
                  >
                    <img
                      src={features[activeFeature].image}
                      alt={features[activeFeature].title}
                      className="w-full h-[500px] object-cover"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t ${features[activeFeature].gradient} opacity-30`} />
                    
                    {/* Floating Stats */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="absolute bottom-6 left-6 right-6 bg-black/80 backdrop-blur-xl rounded-xl p-4 border border-white/20"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-xs text-gray-400 mb-1">Real Impact</div>
                          <div className="font-black text-xl">{features[activeFeature].stat}</div>
                        </div>
                        <TrendingUp className="w-8 h-8 text-green-400" />
                      </div>
                    </motion.div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="relative py-24 px-4 bg-gradient-to-b from-black to-cyan-950/10">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-6xl font-black mb-4">
              Why Choose <span className="text-green-400">Sentlo</span>?
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Compare us with Google Maps and other Nigerian apps
            </p>
          </motion.div>

          <div className="bg-white/5 backdrop-blur-xl rounded-3xl overflow-hidden border border-white/10">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-4 font-bold">Feature</th>
                    <th className="p-4">
                      <div className="flex flex-col items-center gap-2">
                        <Sparkles className="w-6 h-6 text-green-400" />
                        <span className="font-black text-green-400">Sentlo</span>
                      </div>
                    </th>
                    <th className="p-4">
                      <div className="flex flex-col items-center gap-2">
                        <span className="text-2xl">🗺️</span>
                        <span className="text-sm text-gray-500">Google Maps</span>
                      </div>
                    </th>
                    <th className="p-4">
                      <div className="flex flex-col items-center gap-2">
                        <span className="text-2xl">📱</span>
                        <span className="text-sm text-gray-500">Other Apps</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisons.map((row, index) => (
                    <motion.tr
                      key={index}
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.05 }}
                      className="border-b border-white/5 hover:bg-white/5 transition-colors"
                    >
                      <td className="p-4 font-medium">{row.feature}</td>
                      <td className="p-4 text-center">
                        {row.sentlo ? (
                          <div className="flex flex-col items-center gap-1">
                            <CheckCircle className="w-6 h-6 text-green-400" />
                            <span className="text-xs text-gray-500">{row.sentloDetails}</span>
                          </div>
                        ) : (
                          <X className="w-6 h-6 text-gray-600 mx-auto" />
                        )}
                      </td>
                      <td className="p-4 text-center">
                        {row.google ? (
                          <CheckCircle className="w-6 h-6 text-gray-500 mx-auto" />
                        ) : (
                          <X className="w-6 h-6 text-gray-600 mx-auto" />
                        )}
                      </td>
                      <td className="p-4 text-center">
                        {row.others ? (
                          <CheckCircle className="w-6 h-6 text-gray-500 mx-auto" />
                        ) : (
                          <X className="w-6 h-6 text-gray-600 mx-auto" />
                        )}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <div className="inline-block px-8 py-6 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-2xl backdrop-blur-xl">
              <h3 className="text-2xl font-black text-green-400 mb-2">
                Built for Nigeria, By Nigerians
              </h3>
              <p className="text-gray-400 mb-4">
                We understand your daily challenges because we face them too
              </p>
              <button
                onClick={() => navigate('/app')}
                className="px-6 py-3 bg-green-500 hover:bg-green-600 rounded-xl font-bold transition-all"
              >
                Experience The Difference →
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Social Proof - Testimonials */}
      <section id="testimonials" className="relative py-24 px-4 bg-gradient-to-b from-black to-blue-950/20">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-6xl font-black mb-4">
              Loved by <span className="text-green-400">10,000+</span> Nigerians
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Real stories from real people who transformed their daily lives with Sentlo
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-6 border border-white/10 hover:border-green-500/50 transition-all group"
              >
                {/* Rating */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">{testimonial.date}</span>
                </div>

                {/* Quote */}
                <p className="text-gray-300 mb-6 leading-relaxed">
                  "{testimonial.text}"
                </p>

                {/* Savings Badge */}
                <div className="inline-block px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-full text-sm font-bold text-green-400 mb-4">
                  💰 {testimonial.savings}
                </div>

                {/* Author */}
                <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                  <div className="text-4xl">{testimonial.avatar}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-bold">{testimonial.name}</div>
                      {testimonial.verified && (
                        <CheckCircle className="w-4 h-4 text-green-400" />
                      )}
                    </div>
                    <div className="text-sm text-gray-500">{testimonial.role}</div>
                    <div className="text-xs text-gray-600 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {testimonial.location}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <button
              onClick={() => navigate('/app')}
              className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl font-bold text-lg shadow-lg shadow-green-500/50 hover:shadow-green-500/80 hover:scale-105 transition-all"
            >
              Join These Happy Users →
            </button>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="relative py-24 px-4 bg-gradient-to-b from-black to-purple-950/20">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-6xl font-black mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
              Start free forever. Upgrade when you're ready for more power.
            </p>
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-green-500/10 border border-green-500/30 rounded-full">
              <Gift className="w-5 h-5 text-green-400" />
              <span className="text-green-400 font-bold">Get 2 months FREE with annual billing!</span>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-8 border ${
                  plan.popular ? 'border-green-500 shadow-2xl shadow-green-500/20 scale-105' : 'border-white/10'
                } hover:scale-105 transition-all`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full text-sm font-black shadow-lg">
                    ⭐ MOST POPULAR
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-black mb-2">{plan.name}</h3>
                  <div className="text-5xl font-black mb-2">
                    {plan.price}
                  </div>
                  <div className="text-sm text-gray-500">{plan.period}</div>
                  <p className="text-gray-400 text-sm mt-4">{plan.description}</p>
                  {plan.savings && (
                    <div className="mt-4 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-full text-sm font-bold text-green-400">
                      {plan.savings}
                    </div>
                  )}
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      {feature.included ? (
                        <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      ) : (
                        <X className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                      )}
                      <span className={`text-sm ${feature.included ? 'text-gray-300' : 'text-gray-600'}`}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => navigate('/app')}
                  className={`w-full py-4 rounded-xl font-bold transition-all ${
                    plan.popular
                      ? `bg-gradient-to-r ${plan.gradient} hover:shadow-lg hover:shadow-green-500/50`
                      : 'bg-white/10 hover:bg-white/20'
                  }`}
                >
                  {plan.cta}
                </button>
              </motion.div>
            ))}
          </div>

          <div className="text-center text-sm text-gray-500">
            <p className="mb-2">All plans include 24/7 customer support • 99.9% uptime SLA • Nigerian Naira billing</p>
            <p>Need a custom enterprise plan? <button onClick={() => navigate('/app')} className="text-green-400 hover:text-green-300 font-bold">Contact our sales team →</button></p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="relative py-24 px-4 bg-black">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-6xl font-black mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-400">
              Everything you need to know about Sentlo
            </p>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <FAQItem key={index} faq={faq} index={index} />
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12 bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10"
          >
            <MessageCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h3 className="text-2xl font-black mb-2">Still Have Questions?</h3>
            <p className="text-gray-400 mb-6">
              Our support team is here to help you 24/7
            </p>
            <button
              onClick={() => navigate('/app')}
              className="px-6 py-3 bg-green-500 hover:bg-green-600 rounded-xl font-bold transition-all"
            >
              Chat With Support →
            </button>
          </motion.div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="relative py-24 px-4 bg-gradient-to-b from-black to-indigo-950/20">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Bell className="w-16 h-16 text-indigo-400 mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-black mb-4">
              Get Early Access to New Features
            </h2>
            <p className="text-xl text-gray-400 mb-8">
              Join 5,000+ subscribers getting weekly updates on new features, tips, and community stories
            </p>

            <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="flex-1 px-6 py-4 bg-white/10 backdrop-blur-xl rounded-xl border border-white/20 focus:outline-none focus:border-green-500 transition-colors"
              />
              <button
                type="submit"
                className="px-8 py-4 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl font-bold hover:shadow-lg hover:shadow-indigo-500/50 transition-all whitespace-nowrap"
              >
                Subscribe Free →
              </button>
            </form>

            <p className="text-sm text-gray-500 mt-4">
              No spam, ever. Unsubscribe anytime. 🇳🇬
            </p>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-32 px-4 bg-gradient-to-b from-black via-green-950/20 to-black">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative"
          >
            {/* Glow Effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-600 rounded-3xl blur-3xl opacity-30" />
            
            <div className="relative bg-gradient-to-br from-green-600 to-emerald-700 rounded-3xl p-12 md:p-16 shadow-2xl">
              <Sparkles className="w-16 h-16 mx-auto mb-6 animate-pulse" />
              <h2 className="text-4xl md:text-6xl font-black mb-6">
                Ready to Transform Your Daily Life?
              </h2>
              <p className="text-xl md:text-2xl mb-8 opacity-90 max-w-3xl mx-auto">
                Join <span className="font-black">10,045 Nigerians</span> who save 3.5 hours weekly and ₦25,000 monthly with Sentlo
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <button
                  onClick={() => navigate('/app')}
                  className="group px-12 py-6 bg-white text-green-600 rounded-2xl font-black text-xl shadow-xl hover:shadow-2xl hover:scale-105 transition-all flex items-center gap-3 justify-center"
                >
                  <Smartphone className="w-6 h-6" />
                  <span>Start Free Today</span>
                  <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
                </button>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-6 text-sm opacity-90">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <span>Free forever plan</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <span>No credit card</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <span>Setup in 30 seconds</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <span>Cancel anytime</span>
                </div>
              </div>

              {/* Social Proof */}
              <div className="mt-12 pt-8 border-t border-white/20">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <div className="flex -space-x-2">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 border-2 border-white flex items-center justify-center text-xl">👨🏿</div>
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 border-2 border-white flex items-center justify-center text-xl">👩🏾</div>
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 border-2 border-white flex items-center justify-center text-xl">👨🏾</div>
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-red-500 border-2 border-white flex items-center justify-center text-xl">👩🏿</div>
                  </div>
                  <div className="text-left">
                    <div className="font-bold">1,234 people signed up this week</div>
                    <div className="text-sm opacity-75">Don't miss out on what they're experiencing</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12 px-4 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-5 gap-8 mb-12">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="w-8 h-8 text-green-400" />
                <span className="text-2xl font-black bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">SENTLO</span>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-4">
                Nigeria's hyperlocal intelligence network for navigating daily urban challenges. Built by Nigerians, for Nigerians. 🇳🇬
              </p>
              <div className="flex gap-3">
                <a href="#" className="w-10 h-10 bg-white/5 hover:bg-white/10 rounded-lg flex items-center justify-center transition-colors">
                  <Twitter className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 bg-white/5 hover:bg-white/10 rounded-lg flex items-center justify-center transition-colors">
                  <Facebook className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 bg-white/5 hover:bg-white/10 rounded-lg flex items-center justify-center transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 bg-white/5 hover:bg-white/10 rounded-lg flex items-center justify-center transition-colors">
                  <Linkedin className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 bg-white/5 hover:bg-white/10 rounded-lg flex items-center justify-center transition-colors">
                  <Youtube className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Product */}
            <div>
              <h3 className="font-bold mb-4">Product</h3>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="/app" className="hover:text-white transition-colors">Launch App</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Mobile Apps</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API Docs</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Roadmap</a></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="font-bold mb-4">Company</h3>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><a href="#" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Press Kit</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Partners</a></li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h3 className="font-bold mb-4">Resources</h3>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Community</a></li>
                <li><a href="#testimonials" className="hover:text-white transition-colors">Reviews</a></li>
                <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Status</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Changelog</a></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-500">
            <div>© 2026 Sentlo Technologies Ltd. All rights reserved. Made with ❤️ in Nigeria 🇳🇬</div>
            <div className="flex gap-6">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-white transition-colors">Security</a>
              <a href="#" className="hover:text-white transition-colors">Cookies</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Video Modal */}
      <AnimatePresence>
        {isVideoPlaying && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 backdrop-blur-xl z-50 flex items-center justify-center p-4"
            onClick={() => setIsVideoPlaying(false)}
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              className="relative max-w-5xl w-full aspect-video bg-gray-900 rounded-3xl overflow-hidden border-2 border-green-500/50 shadow-2xl shadow-green-500/20"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Demo Video Placeholder - Blank for now */}
              <div className="w-full h-full flex items-center justify-center">
                <p className="text-gray-500 text-lg">Demo video coming soon...</p>
              </div>
              
              <button
                onClick={() => setIsVideoPlaying(false)}
                className="absolute top-4 right-4 w-12 h-12 bg-black/80 hover:bg-black rounded-full flex items-center justify-center transition-colors backdrop-blur-sm border border-white/20 z-10"
              >
                <X className="w-6 h-6" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Action Button */}
      <motion.button
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 2 }}
        onClick={() => navigate('/app')}
        className="fixed bottom-8 right-8 w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full shadow-2xl shadow-green-500/50 flex items-center justify-center hover:scale-110 transition-all z-40 animate-pulse"
      >
        <ArrowRight className="w-8 h-8 text-white" />
      </motion.button>
    </div>
  );
}

// FAQ Item Component
function FAQItem({ faq, index }: { faq: { question: string; answer: string }; index: number }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden hover:border-green-500/30 transition-all"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
      >
        <span className="font-bold text-lg pr-4">{faq.question}</span>
        <ChevronDown
          className={`w-6 h-6 text-green-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-5 text-gray-400 leading-relaxed">
              {faq.answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// CountUp Component
function CountUp({ end, suffix = '' }: { end: number; suffix?: string }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const increment = end / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(current);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [end]);

  return (
    <>
      {count.toFixed(end % 1 !== 0 ? 1 : 0)}
      {suffix}
    </>
  );
}
